import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_ELSTIC_URL } from "../../config";
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Chip,
  Typography,
  Box,
  Paper,
} from "@mui/material";

const ManageAttorneyTrademarkData = () => {
  const { id } = useParams();
  const attorneyId = decodeURIComponent(id);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50;
//   const limit = 10;

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-all-trademark-data`,
        {
          params: {
            search: "attorney_name",
            query: attorneyId,
            limit: 50, // API gives max 50 in one call
            page: 1,
          },
        }
      );
      setData(res.data.data);
      setTotalPages(Math.ceil(res.data.data.total / limit));
    } catch (err) {
      console.error("Error fetching attorney data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [attorneyId, currentPage]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No data found</Typography>
      </Box>
    );
  }

  const { trademarks, aggregations, total } = data;

//   // Pagination logic
//   const startIndex = (currentPage - 1) * limit;
//   const paginatedTrademarks = trademarks.slice(startIndex, startIndex + limit);
//   const totalPages = Math.ceil(trademarks.length / limit);

  const renderAggregationTable = (title, buckets) => (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader title={title} />
      <CardContent sx={{ p: 0 }}>
        <TableContainer sx={{ maxHeight: 250, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Key</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buckets && buckets.length > 0 ? (
                buckets.map((b, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{b.key}</TableCell>
                    <TableCell>
                      <Chip label={b.doc_count} color="primary" size="small" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Page Header */}
          <div className="row mb-4">
            <div className="col-12">
              <Typography variant="h4" fontWeight="bold">
                Attorney Data – {attorneyId}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Total Trademarks: {total}
              </Typography>
            </div>
          </div>

          {/* Aggregations Section */}
          <div className="row">
            <div className="col-md-6 col-lg-4">
              {renderAggregationTable(
                "Status Breakdown",
                aggregations.status_breakdown?.buckets || []
              )}
            </div>
            <div className="col-md-6 col-lg-4">
              {renderAggregationTable(
                "Alert Breakdown",
                aggregations.alert_breakdown?.buckets || []
              )}
            </div>
            <div className="col-md-6 col-lg-4">
              {renderAggregationTable(
                "Proprietor Breakdown",
                aggregations.proprietor_breakdown?.buckets || []
              )}
            </div>
            <div className="col-md-6 col-lg-4">
              {renderAggregationTable(
                "Class Breakdown",
                aggregations.class_breakdown?.buckets || []
              )}
            </div>
            <div className="col-md-6 col-lg-4">
              {renderAggregationTable(
                "State Breakdown",
                aggregations.state_breakdown?.buckets || []
              )}
            </div>
          </div>

          {/* Trademark Table */}
          <Card variant="outlined" sx={{ mt: 4 }}>
            <CardHeader title={`Trademarks (${total})`} />
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Logo</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>TM Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date of Application</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Proprietor</TableCell>
                      <TableCell>Attorney</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trademarks?.map((tm) => (
                      <TableRow key={tm.id}>
                        <TableCell>
                          {tm.logo_url ? (
                            <img
                              src={tm.logo_url}
                              alt={tm.name}
                              style={{
                                width: "45px",
                                height: "45px",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{tm.name}</TableCell>
                        <TableCell>{tm.trade_mark}</TableCell>
                        <TableCell>
                          <Chip
                            label={tm.status}
                            color={
                              tm.status === "Registered"
                                ? "success"
                                : tm.status === "Objected"
                                ? "error"
                                : "warning"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(tm.date_of_application).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip label={tm.class} color="info" size="small" />
                        </TableCell>
                        <TableCell>{tm.proprietor_name}</TableCell>
                        <TableCell>{tm.attorney_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ManageAttorneyTrademarkData;
